import * as styles from "./ComboboxContent.css";

import { clsx } from "clsx";
import { type JSX, useState } from "react";

import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { popupWidthStyle } from "@frontend/utils/popupWidth";

import type { ComboboxContentProps } from "./Combobox.types";
import { ComboboxEmpty } from "./ComboboxEmpty";
import { ComboboxPortal } from "./ComboboxPortal";
import { ComboboxScrollContext } from "./ComboboxScroll.context";

export function ComboboxContent({
  className,
  children,
  input,
  empty,
  popupWidth,
  style,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignOffset,
  anchor,
  ...props
}: ComboboxContentProps): JSX.Element {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);

  return (
    <ComboboxPortal>
      <BaseCombobox.Positioner
        data-slot="combobox-positioner"
        className={styles.positioner}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        anchor={anchor}
      >
        <BaseCombobox.Popup
          data-slot="combobox-content"
          className={clsx(styles.popup, className)}
          style={(state) => ({
            ...popupWidthStyle(popupWidth),
            ...(typeof style === "function" ? style(state) : style),
          })}
          {...props}
        >
          {/* Input inside popup */}
          {input}

          {empty !== undefined ? (
            <ComboboxEmpty className={styles.empty}>{empty}</ComboboxEmpty>
          ) : null}

          <ScrollArea className={styles.scrollArea}>
            <ScrollAreaViewport ref={setViewport} className={styles.viewport}>
              <ComboboxScrollContext.Provider value={viewport}>
                <BaseCombobox.List
                  data-slot="combobox-list"
                  className={styles.list}
                >
                  {children}
                </BaseCombobox.List>
              </ComboboxScrollContext.Provider>
            </ScrollAreaViewport>

            <ScrollAreaScrollbar>
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>
        </BaseCombobox.Popup>
      </BaseCombobox.Positioner>
    </ComboboxPortal>
  );
}
